import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { tokens } = await fetchAuthSession();
    const idToken = tokens?.idToken?.toString();
    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  } catch {
    // Not authenticated or local dev without Cognito — omit header
  }
  return {};
}

export async function apiGet<T>(path: string): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || `Request failed: ${response.status}`);
  }
  return response.json();
}

// The Lambda handler wraps responses as { statusCode, body }. A validation
// failure can come back as an HTTP 200 whose envelope carries an error
// statusCode, so checking response.ok alone would treat a failed save as a
// success. Throw when the enveloped statusCode is an error, surfacing the real
// message instead of a false "saved" toast.
function assertEnvelopeOk(parsed: unknown): void {
  if (!parsed || typeof parsed !== 'object') return;
  const envelope = parsed as { statusCode?: number; body?: unknown; message?: string };
  if (typeof envelope.statusCode !== 'number' || envelope.statusCode < 400) return;
  const errBody = envelope.body as { message?: string; detail?: string } | string | undefined;
  const detail =
    errBody && typeof errBody === 'object'
      ? errBody.message ?? errBody.detail ?? JSON.stringify(errBody)
      : errBody;
  throw new Error(
    (typeof detail === 'string' && detail) || envelope.message || `Request failed: ${envelope.statusCode}`
  );
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.message || `Request failed: ${response.status}`);
  }
  const parsed = await response.json();
  assertEnvelopeOk(parsed);
  return parsed;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.message || `Request failed: ${response.status}`);
  }
  const parsed = await response.json();
  assertEnvelopeOk(parsed);
  return parsed;
}

export async function apiDelete(path: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  });
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.message || `Request failed: ${response.status}`);
  }
}
